/* Модель экрана «Повтори движения» / «Стать персонажем» (APP-SPEC-5 §20, 03-api.md §9.2 «dance»): пресеты, пределы фрагмента,
   строка состояния, чего не хватает, цена и тексты итога. Всё, что зависит от сервера, читается из формы (policy motion-n8n-v1)
   и цены приложения (ToolPrice); чего сервер не публикует — модель не утверждает. */
import type { StudioApp, StudioForm } from '../studio/studio-api';
import { studioExpectedPrice, studioUnitPrice } from '../studio/studio-api';
import { record } from '../studio/studio-content';
import { toolField } from '../studio/circle/tool-form';
import { toolPrice } from '../studio/circle/tool-helpers';
import { mmss } from '../studio/circle/order-tail-model';

export type MotionPreset = 'dance' | 'swap';
export type MotionHeroKind = 'me' | 'avatar' | 'person' | 'char' | 'ph';
export type MotionVoiceMode = 'src' | 'hero' | 'voice';

/** Слова пресета (bot-dance.js PRE): название, подписи блоков, пустая рамка, камера записи, строки проверки и кнопки. */
export interface MotionWords {
  title: string;
  clipLabel: string;
  heroLabel: string;
  upload: string;
  sub: string;
  camera: 'environment' | 'user';
  body: string;
  who: string;
  remain: string;
  sheet: string;
}
const WORDS: Record<MotionPreset, MotionWords> = {
  dance: {
    title: 'Повтори движения',
    clipLabel: '1. Видео с движениями',
    heroLabel: '2. Кто повторит',
    upload: 'Загрузите видео с движениями',
    sub: 'Танец или тренд, один человек в полный рост, от {0} до {1} секунд',
    camera: 'environment',
    body: 'Не нашли человека: нужен один человек в полный рост',
    who: 'Выберите, кто повторит',
    remain: 'Осталось выбрать, кто повторит',
    sheet: 'Кто повторит движения',
  },
  swap: {
    title: 'Стать персонажем',
    clipLabel: '1. Видео с вами',
    heroLabel: '2. Кем стать',
    upload: 'Загрузите видео с собой',
    sub: 'По пояс или в полный рост, от {0} до {1} секунд',
    camera: 'user',
    body: 'Не нашли человека: нужен один человек по пояс или в полный рост',
    who: 'Выберите, кем стать',
    remain: 'Осталось выбрать, кем стать',
    sheet: 'Кем вы станете в видео',
  },
};
export function motionWords(preset: MotionPreset | undefined): MotionWords {
  return WORDS[preset === 'swap' ? 'swap' : 'dance'];
}
/** Подстановка {0}, {1} без перевода (перевод делает экран). */
export const fill = (text: string, ...values: Array<string | number>) => text.replace(/\{(\d)\}/g, (_, i) => String(values[Number(i)] ?? ''));

export function resetHeroCrop(inputs: Record<string, string>) {
  const next = { ...inputs };
  for (const key of ['crop_x', 'crop_y', 'zoom']) delete next[key];
  return next;
}
export function motionPolicy(form: StudioForm) {
  const p = record(form.constraints?.motion);
  return p.policy_version === 'motion-n8n-v1' && ['dance', 'swap'].includes(String(p.preset))
    ? (p as Record<string, unknown> & { preset: MotionPreset })
    : null;
}
export function motionLimits(form: StudioForm, inputs: Record<string, string>) {
  const p = motionPolicy(form),
    bg = inputs.bg === 'hero' ? 'hero' : 'video';
  const limit = record(record(p?.duration_limits)[bg]);
  return {
    minMs: Math.max(bg === 'hero' ? 3000 : 2000, Number(limit.min_ms) || 0),
    maxMs: Math.min(bg === 'hero' ? 30000 : 15000, Number(limit.max_ms) || Infinity),
  };
}
/** Фрагмент ролика из полей start_ms / end_ms; нет конца — null. */
export function motionFragment(inputs: Record<string, string>) {
  const start = Number(inputs.start_ms ?? 0),
    end = Number(inputs.end_ms);
  if (!Number.isFinite(start) || !Number.isFinite(end) || inputs.end_ms === undefined || inputs.end_ms === '') return null;
  return { startMs: start, endMs: end, lenMs: end - start };
}
/** Кто повторит по значениям полей: cast ('self' · 'user_avatar:<id>' · 'person:<id>' · 'character:<id>') или разовое фото. */
export function motionHeroKind(inputs: Record<string, string>): MotionHeroKind | null {
  if (inputs.photo) return 'ph';
  const cast = inputs.cast ?? '';
  if (cast === 'self') return 'me';
  if (cast.startsWith('user_avatar:')) return 'avatar';
  if (cast.startsWith('person:')) return 'person';
  if (cast.startsWith('character:')) return 'char';
  return null;
}
/** Сведения о выбранном герое, которые знает только ряд лиц (имя, нет фото). */
export interface MotionHeroInfo {
  name: string;
  needsPhoto?: boolean;
}
/** Публикуемые значения голоса: из поля voice формы и только при policy.voice_change_available для смены голоса. */
export function motionVoiceModes(form: StudioForm): MotionVoiceMode[] {
  const field = toolField(form, 'voice', 'voice_mode');
  if (!field) return [];
  const policy = motionPolicy(form);
  const values = (field.values.length ? field.values : ['src']).filter((v): v is MotionVoiceMode => ['src', 'hero', 'voice'].includes(v));
  return values.filter(v => v === 'src' || (policy?.voice_change_available === true && (v !== 'voice' || !!toolField(form, 'voice_id'))));
}
/** Ключ поля голоса формы: voice (шаблон n8n) или voice_mode (контракт экрана §9.2). */
export const motionVoiceKey = (form: StudioForm) => (toolField(form, 'voice') ? 'voice' : 'voice_mode');
/** Голос меняется (надбавка, этапы «Меняем голос» и «Синхронизируем губы»). */
export function motionVoiceChange(form: StudioForm, inputs: Record<string, string>) {
  const value = inputs[motionVoiceKey(form)];
  return !!value && value !== 'src';
}

export interface MotionMissingContext {
  hero?: MotionHeroInfo | null;
  cropOpen?: boolean;
}
export function motionMissing(form: StudioForm, inputs: Record<string, string>, rights: boolean, ctx: MotionMissingContext = {}) {
  const policy = motionPolicy(form);
  if (!policy || !form.available) return 'Создание сейчас недоступно';
  const words = motionWords(policy.preset);
  if (ctx.cropOpen) return 'Закончите кадр фото';
  if (!inputs.video) return words.upload;
  if (inputs.cast && inputs.photo) return 'Выберите одного героя';
  if (!inputs.cast && !inputs.photo) return words.who;
  if (ctx.hero?.needsPhoto) return fill('Добавьте фото: {0}', ctx.hero.name);
  const { minMs, maxMs } = motionLimits(form, inputs),
    fragment = motionFragment(inputs);
  if (!fragment || fragment.startMs < 0 || fragment.lenMs < minMs || fragment.lenMs > maxMs)
    return fill('Выберите фрагмент от {0} до {1} секунд', minMs / 1000, maxMs / 1000);
  const voiceKey = motionVoiceKey(form),
    voice = inputs[voiceKey];
  if (voice && voice !== 'src' && !motionVoiceModes(form).includes(voice as MotionVoiceMode)) return 'Смена голоса пока недоступна';
  if (voice === 'voice' && !inputs.voice_id) return 'Выберите голос';
  if (inputs.bg && !['video', 'hero'].includes(inputs.bg)) return 'Выберите фон';
  if (!rights || inputs.consent_accepted !== 'true') return 'Подтвердите права и согласие человека';
  return form.fields.some(f => f.required && !['cast', 'photo'].includes(f.key) && !inputs[f.key]?.trim())
    ? 'Заполните обязательные поля'
    : null;
}
export function motionDefaults(form: StudioForm) {
  const p = motionPolicy(form);
  if (!p) return {};
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries({ bg: p.default_bg ?? 'video', voice: 'src', preset: p.preset }))
    if (form.fields.some(f => f.key === key && (!f.values.length || f.values.includes(String(value))))) result[key] = String(value);
  return result;
}

/** Строка состояния под рамкой (§20.1, таблица). Без ролика строки нет. «Человек в кадре» — только если сервер проверил это (humanFound). */
export interface MotionStatus {
  kind: 'bad' | 'mute' | 'ok';
  text: string;
}
export function motionStatus(
  form: StudioForm,
  inputs: Record<string, string>,
  clip: { durationMs: number } | null,
  hero: MotionHeroInfo | null,
  humanFound: boolean | null = null,
): MotionStatus | null {
  const policy = motionPolicy(form);
  if (!policy || !inputs.video) return null;
  const words = motionWords(policy.preset);
  const { minMs, maxMs } = motionLimits(form, inputs);
  const min = minMs / 1000,
    max = maxMs / 1000;
  if (clip && clip.durationMs > 0 && clip.durationMs < minMs)
    return { kind: 'bad', text: fill('Видео короче {0} секунд: нужно от {0} до {1} секунд', min, max) };
  if (humanFound === false) return { kind: 'bad', text: words.body };
  const fragment = motionFragment(inputs);
  if (fragment && fragment.lenMs < minMs) return { kind: 'bad', text: fill('Фрагмент короче {0} секунд: растяните окно', min) };
  if (fragment && fragment.lenMs > maxMs) return { kind: 'bad', text: fill('Фрагмент длиннее {0} секунд: сузьте окно', max) };
  if (!inputs.cast && !inputs.photo) return { kind: 'mute', text: words.remain };
  if (hero?.needsPhoto) return { kind: 'mute', text: fill('Осталось добавить фото: {0}', hero.name) };
  if (!fragment) return null;
  const cut = !!clip && (fragment.startMs > 0 || fragment.endMs < clip.durationMs - 500);
  const length = `${cut ? 'Фрагмент' : 'Длительность'} ${mmss(fragment.lenMs / 1000)}`;
  return { kind: 'ok', text: humanFound === true ? `Человек в кадре · ${length}` : length };
}
/** Проверку человека в кадре делает сервер (policy.human_check_available); пока он её не публикует — результата нет. */
export function motionHumanFound(form: StudioForm, check: unknown): boolean | null {
  if (motionPolicy(form)?.human_check_available !== true) return null;
  const value = record(check).human_found;
  return typeof value === 'boolean' ? value : null;
}

/** Цена за секунду фрагмента: платная форма — прайс сервера (unit_price за секунду), иначе цена приложения
    (ToolPrice per_second, 03-api.md §6.7); нет — null. */
export function motionUnit(app: StudioApp | null | undefined, form?: StudioForm | null): number | null {
  const listed = studioUnitPrice(form, 'second');
  if (listed !== null) return listed;
  const price = toolPrice(app?.price);
  return price.unit === 'per_second' && price.amount !== null && price.amount > 0 ? price.amount : null;
}
/** Надбавка за смену голоса, C в минуту — только если её публикует политика формы. */
export function motionVoiceUnit(form: StudioForm): number | null {
  const value = Number(motionPolicy(form)?.voice_price_per_minute);
  return Number.isFinite(value) && value > 0 ? value : null;
}
/** Минимум оплачиваемой длины (§17: «не меньше 3 с»). */
export const MOTION_MIN_BILLED_S = 3;
/** Цена на кнопке: по серверной единице и длине фрагмента (оценка; сервер проверит expected_price), иначе expected_price формы. */
export function motionPrice(app: StudioApp | null | undefined, form: StudioForm, inputs: Record<string, string>) {
  const unit = motionUnit(app, form);
  if (unit === null || !form.chargeable) return { price: studioExpectedPrice(form, inputs), from: false, estimate: false };
  const fragment = inputs.video ? motionFragment(inputs) : null;
  const seconds = Math.max(MOTION_MIN_BILLED_S, fragment ? Math.round(fragment.lenMs / 1000) : 0);
  const voice = motionVoiceUnit(form);
  const extra = motionVoiceChange(form, inputs) && voice !== null ? Math.ceil((seconds * voice) / 60) : 0;
  return { price: unit * seconds + extra, from: !fragment, estimate: true };
}

/** Пропорции ролика словом (bot-dance.js arName / AR_W). */
const RATIOS: Array<[string, number]> = [
  ['9:16', 9 / 16],
  ['16:9', 16 / 9],
  ['1:1', 1],
  ['4:5', 4 / 5],
  ['3:4', 3 / 4],
];
const RATIO_WORDS: Record<string, string> = { '9:16': 'Сторис', '16:9': 'Горизонтальное', '1:1': 'Квадрат', '4:5': 'Пост', '3:4': 'Портрет' };
export function motionRatioName(width: number, height: number) {
  const r = (width || 9) / (height || 16);
  return RATIOS.reduce((best, item) => (Math.abs(Math.log(item[1] / r)) < Math.abs(Math.log(best[1] / r)) ? item : best), RATIOS[0])[0];
}
export const motionRatioWord = (width: number, height: number) => RATIO_WORDS[motionRatioName(width, height)];

/** Снимок входа для хвоста: кто повторит словами, род, ролик, формат, длина, фон, голос. */
export interface MotionSnapshot {
  preset: MotionPreset;
  kind: MotionHeroKind | null;
  name: string;
  female: boolean;
  fileName: string;
  width: number;
  height: number;
  lenMs: number;
  bg: 'video' | 'hero';
  voiceName: string;
}
export function motionCardText(s: MotionSnapshot) {
  return [
    s.name,
    s.fileName ? `видео ${s.fileName}` : '',
    s.width && s.height ? motionRatioWord(s.width, s.height) : '',
    s.lenMs > 0 ? mmss(s.lenMs / 1000) : '',
    s.bg === 'hero' ? 'фон с героя' : '',
    s.voiceName ? `голос «${s.voiceName}»` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}
/** Подпись итога (bot-dance.js doneText): у танца — кто повторил; у замены — герой в вашем видео. Звук — только если сервер его сохраняет. */
export function motionDoneText(s: MotionSnapshot, audio: boolean, delivered: boolean) {
  const sound = s.voiceName ? `голос «${s.voiceName}»` : audio ? 'звук как в видео' : '';
  const tail = delivered ? ' Бот уже прислал видео в чат, в отдельную тему.' : '';
  const who = s.kind === 'me' || s.kind === 'avatar' ? 'Вы' : s.kind === 'ph' ? 'Человек с фото' : s.name || 'Герой';
  if (s.preset === 'swap') return `${who} теперь в вашем видео: движения и мимика ваши${sound ? ', ' + sound : ''}.${tail}`;
  const verb = who === 'Вы' ? 'Вы повторили' : `${who} ${s.female ? 'повторила' : 'повторил'}`;
  const rest = s.voiceName ? `${sound}, формат тот же` : audio ? 'звук и формат те же' : 'формат тот же';
  return `${verb} движения из видео, ${rest}.${tail}`;
}

/** Вход экрана (07-apps.md §10, BotDance.open): who, char, chars, pid, voice, bg → поля формы. Ключи формы входа главнее. */
export function motionEntryInputs(form: StudioForm, entry: Record<string, unknown> | undefined): Record<string, string> {
  if (!entry) return {};
  const out: Record<string, string> = {};
  const has = (key: string) => !!toolField(form, key);
  const id = (value: unknown) => (Number.isSafeInteger(Number(value)) && Number(value) > 0 ? String(Number(value)) : '');
  if (has('cast') && typeof entry.cast !== 'string') {
    const chars = Array.isArray(entry.chars) ? entry.chars.map(id).filter(Boolean) : [];
    if (id(entry.char)) out.cast = `character:${id(entry.char)}`;
    else if (id(entry.pid)) out.cast = `person:${id(entry.pid)}`;
    else if (entry.who === 'me') out.cast = 'self';
    else if (entry.who === 'char' && chars[0]) out.cast = `character:${chars[0]}`;
  }
  const bg = toolField(form, 'bg');
  if (bg && typeof entry.bg === 'string' && bg.values.includes(entry.bg)) out.bg = entry.bg;
  const voiceKey = motionVoiceKey(form);
  if (typeof entry.voice === 'string' || typeof entry.voice === 'number') {
    const modes = motionVoiceModes(form);
    const value = String(entry.voice);
    if (modes.includes(value as MotionVoiceMode)) out[voiceKey] = value;
    else if (id(value) && modes.includes('voice')) {
      out[voiceKey] = 'voice';
      out.voice_id = id(value);
    }
  }
  const handled = new Set(['who', 'char', 'chars', 'pid', 'voice', 'bg', 'file', 'preset']);
  for (const [key, value] of Object.entries(entry)) if (typeof value === 'string' && has(key) && !handled.has(key)) out[key] = value;
  return out;
}
/** Персонажи входа, которые встают первыми в ряду (o.chars). */
export function motionEntryChars(entry: Record<string, unknown> | undefined): number[] {
  return Array.isArray(entry?.chars) ? entry.chars.map(Number).filter(v => Number.isSafeInteger(v) && v > 0) : [];
}
